/**
 * Google Healthcare API Service
 * Handles all interactions with the Healthcare API FHIR store
 */

const HEALTHCARE_API_BASE = 'https://healthcare.googleapis.com/v1';
const PROJECT_ID = 'cursor531d2r';
const LOCATION = 'us-central1';
const DATASET_ID = 'demo-hospital-ehr';
const FHIR_STORE_ID = 'demo-patients';

// FHIR store base URL
const FHIR_BASE = `${HEALTHCARE_API_BASE}/projects/${PROJECT_ID}/locations/${LOCATION}/datasets/${DATASET_ID}/fhirStores/${FHIR_STORE_ID}/fhir`;

/**
 * Get access token for Healthcare API
 * In production, this would come from your authentication provider
 */
const getAccessToken = async () => {
  // For now, we'll use gcloud auth token via a backend proxy
  // In production, implement proper OAuth2 flow
  try {
    const response = await fetch('/api/auth/token');
    const data = await response.json();
    return data.accessToken;
  } catch (error) {
    console.error('Failed to get access token:', error);
    // Fallback for development - user must run: gcloud auth print-access-token
    return null;
  }
};

/**
 * Make authenticated request to Healthcare API
 */
const makeHealthcareRequest = async (url, options = {}) => {
  const token = await getAccessToken();
  
  const headers = {
    'Content-Type': 'application/fhir+json',
    ...options.headers,
  };
  
  // Only add auth header if we have a token
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(url, {
    ...options,
    headers,
  });
  
  if (!response.ok) {
    throw new Error(`Healthcare API error: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Search for patients
 */
export const searchPatients = async (searchTerm = '', page = 1, limit = 20) => {
  try {
    const offset = (page - 1) * limit;
    let url = `${FHIR_BASE}/Patient?_count=${limit}`;
    
    if (offset > 0) {
      // Healthcare API uses _offset for pagination
      url += `&_offset=${offset}`;
    }
    
    if (searchTerm) {
      // Search by name (given or family)
      url += `&name=${encodeURIComponent(searchTerm)}`;
    }
    
    const response = await makeHealthcareRequest(url);
    
    // Transform FHIR bundle to our app format
    const patients = (response.entry || []).map(entry => {
      const resource = entry.resource;
      const name = resource.name?.[0];
      const nameText = name ? 
        `${name.given?.join(' ') || ''} ${name.family || ''}`.trim() : 
        'Unknown Patient';
      
      // Calculate age from birthDate
      const birthDate = resource.birthDate;
      let age = null;
      if (birthDate) {
        const birth = new Date(birthDate);
        const today = new Date();
        age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
          age--;
        }
      }
      
      return {
        id: resource.id,
        name: nameText,
        birthDate: birthDate || 'Unknown',
        age: age || 'Unknown',
        gender: resource.gender || 'Unknown',
        mrn: resource.identifier?.find(id => id.type?.coding?.[0]?.code === 'MR')?.value || resource.id,
        // For demo purposes, create mock encounter data
        // In production, fetch actual Encounter resources
        activeEncounter: {
          status: 'in-progress',
          class: 'emergency',
          esiLevel: Math.floor(Math.random() * 5) + 1,
          reasonForVisit: 'Emergency Department Visit'
        }
      };
    });
    
    return {
      patients,
      total: response.total || patients.length,
      page,
      limit
    };
  } catch (error) {
    console.error('Error searching patients:', error);
    throw error;
  }
};

/**
 * Get patient by ID
 */
export const getPatient = async (patientId) => {
  try {
    const url = `${FHIR_BASE}/Patient/${patientId}`;
    const resource = await makeHealthcareRequest(url);
    
    const name = resource.name?.[0];
    const nameText = name ? 
      `${name.given?.join(' ') || ''} ${name.family || ''}`.trim() : 
      'Unknown Patient';
    
    // Calculate age
    const birthDate = resource.birthDate;
    let age = null;
    if (birthDate) {
      const birth = new Date(birthDate);
      const today = new Date();
      age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
    }
    
    return {
      id: resource.id,
      name: nameText,
      birthDate: birthDate || 'Unknown',
      age: age || 'Unknown',
      gender: resource.gender || 'Unknown',
      mrn: resource.identifier?.find(id => id.type?.coding?.[0]?.code === 'MR')?.value || resource.id,
      // Add more fields as needed
      telecom: resource.telecom,
      address: resource.address,
      maritalStatus: resource.maritalStatus,
      // Mock encounter for demo
      activeEncounter: {
        status: 'in-progress',
        class: 'emergency',
        esiLevel: Math.floor(Math.random() * 5) + 1,
        reasonForVisit: 'Emergency Department Visit'
      }
    };
  } catch (error) {
    console.error('Error fetching patient:', error);
    throw error;
  }
};

/**
 * Get patient encounters
 */
export const getPatientEncounters = async (patientId) => {
  try {
    const url = `${FHIR_BASE}/Encounter?patient=${patientId}&_sort=-date`;
    const response = await makeHealthcareRequest(url);
    
    return (response.entry || []).map(entry => ({
      id: entry.resource.id,
      status: entry.resource.status,
      class: entry.resource.class?.code,
      type: entry.resource.type?.[0]?.text,
      period: entry.resource.period,
      reasonCode: entry.resource.reasonCode?.[0]?.text
    }));
  } catch (error) {
    console.error('Error fetching encounters:', error);
    return [];
  }
};

/**
 * Get patient conditions
 */
export const getPatientConditions = async (patientId) => {
  try {
    const url = `${FHIR_BASE}/Condition?patient=${patientId}`;
    const response = await makeHealthcareRequest(url);
    
    return (response.entry || []).map(entry => ({
      id: entry.resource.id,
      code: entry.resource.code?.text || entry.resource.code?.coding?.[0]?.display,
      clinicalStatus: entry.resource.clinicalStatus?.coding?.[0]?.code,
      verificationStatus: entry.resource.verificationStatus?.coding?.[0]?.code,
      onsetDateTime: entry.resource.onsetDateTime,
      recordedDate: entry.resource.recordedDate
    }));
  } catch (error) {
    console.error('Error fetching conditions:', error);
    return [];
  }
};

/**
 * Get patient observations (vitals, labs, etc.)
 */
export const getPatientObservations = async (patientId, category = null) => {
  try {
    let url = `${FHIR_BASE}/Observation?patient=${patientId}&_sort=-date`;
    if (category) {
      url += `&category=${category}`;
    }
    
    const response = await makeHealthcareRequest(url);
    
    return (response.entry || []).map(entry => ({
      id: entry.resource.id,
      status: entry.resource.status,
      code: entry.resource.code?.text || entry.resource.code?.coding?.[0]?.display,
      value: entry.resource.valueQuantity || entry.resource.valueString,
      effectiveDateTime: entry.resource.effectiveDateTime,
      category: entry.resource.category?.[0]?.coding?.[0]?.code
    }));
  } catch (error) {
    console.error('Error fetching observations:', error);
    return [];
  }
};

/**
 * Get patient medications
 */
export const getPatientMedications = async (patientId) => {
  try {
    const url = `${FHIR_BASE}/MedicationRequest?patient=${patientId}&_sort=-date`;
    const response = await makeHealthcareRequest(url);
    
    return (response.entry || []).map(entry => ({
      id: entry.resource.id,
      status: entry.resource.status,
      medication: entry.resource.medicationCodeableConcept?.text || 
                 entry.resource.medicationCodeableConcept?.coding?.[0]?.display,
      dosage: entry.resource.dosageInstruction?.[0]?.text,
      authoredOn: entry.resource.authoredOn
    }));
  } catch (error) {
    console.error('Error fetching medications:', error);
    return [];
  }
};

export default {
  searchPatients,
  getPatient,
  getPatientEncounters,
  getPatientConditions,
  getPatientObservations,
  getPatientMedications
}; 